import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  getTreasuryFormSubmissionFilterOptions,
  listTreasuryFormSubmissions,
} from "../api/treasuryFormsApi";
import {
  formatDate,
  formatDateTime,
  getFormTypeLabel,
  getSubmissionStatusLabel,
} from "../utils/treasuryFormsFormatters";

const TreasurySubmissionDetailModal = lazy(() =>
  import("./TreasurySubmissionDetailModal")
);

const SUBMISSIONS_PAGE_SIZE = 25;

function SubmissionStatusChip({ status }) {
  let bgcolor = "rgba(255, 179, 0, 0.18)";
  let color = "warning.dark";

  if (status === "APPROVED") {
    bgcolor = "rgba(185, 246, 202, 0.72)";
    color = "primary.900";
  } else if (status === "CANCELLED") {
    bgcolor = "rgba(239, 83, 80, 0.18)";
    color = "error.main";
  }

  return (
    <Chip
      label={getSubmissionStatusLabel(status)}
      size="small"
      sx={{
        borderRadius: "10px",
        fontWeight: 800,
        bgcolor,
        color,
      }}
    />
  );
}

export default function SolicitudesRecibidasSection() {
  // Filter Options State
  const [filterOptions, setFilterOptions] = useState({
    forms: [],
    employees: [],
    sectors: [],
    periods: [],
  });
  const [filterOptionsError, setFilterOptionsError] = useState("");

  // Filter Values State
  const [formIdFilter, setFormIdFilter] = useState("");
  const [employeeIdFilter, setEmployeeIdFilter] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [operationalDateFilter, setOperationalDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [periodIdFilter, setPeriodIdFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [debouncedSearchFilter, setDebouncedSearchFilter] = useState("");

  // Submissions Data State
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Detail Modal State
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);

  // Load filter options
  const fetchFilterOptions = useCallback(async (signal) => {
    setFilterOptionsError("");
    try {
      const res = await getTreasuryFormSubmissionFilterOptions({ signal });
      if (res) {
        setFilterOptions({
          forms: res.forms || [],
          employees: res.employees || [],
          sectors: res.sectors || [],
          periods: res.periods || [],
        });
      }
    } catch (err) {
      if (signal?.aborted) return;
      setFilterOptionsError(
        err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          "No se pudieron cargar las opciones de los filtros."
      );
    }
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchFilter(searchFilter.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchFilter]);

  // Load submissions
  const fetchSubmissions = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const res = await listTreasuryFormSubmissions({
        page: page + 1,
        limit: SUBMISSIONS_PAGE_SIZE,
        formId: formIdFilter || undefined,
        employeeId: employeeIdFilter || undefined,
        sector: sectorFilter || undefined,
        operationalDate: operationalDateFilter || undefined,
        status: statusFilter || undefined,
        periodId: periodIdFilter || undefined,
        search: debouncedSearchFilter || undefined,
        signal,
      });

      setSubmissions(res?.data || []);
      setTotalItems(res?.meta?.totalItems || 0);
    } catch (err) {
      if (signal?.aborted) return;
      setError(
        err?.response?.data?.message ||
          err?.data?.message ||
          "No se pudieron cargar las solicitudes recibidas."
      );
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [
    page,
    formIdFilter,
    employeeIdFilter,
    sectorFilter,
    operationalDateFilter,
    statusFilter,
    periodIdFilter,
    debouncedSearchFilter,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    fetchFilterOptions(controller.signal);
    return () => controller.abort();
  }, [fetchFilterOptions]);

  useEffect(() => {
    const controller = new AbortController();
    fetchSubmissions(controller.signal);
    return () => controller.abort();
  }, [fetchSubmissions]);

  const handleClearFilters = () => {
    setFormIdFilter("");
    setEmployeeIdFilter("");
    setSectorFilter("");
    setOperationalDateFilter("");
    setStatusFilter("");
    setPeriodIdFilter("");
    setSearchFilter("");
    setPage(0);
  };

  const hasActiveFilters = Boolean(
    formIdFilter ||
      employeeIdFilter ||
      sectorFilter ||
      operationalDateFilter ||
      statusFilter ||
      periodIdFilter ||
      searchFilter
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack spacing={2.5}>
        {/* Filters Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "18px",
            bgcolor: "rgba(255, 255, 255, 0.95)",
            border: "1px solid rgba(20, 32, 82, 0.08)",
          }}
        >
          <Stack spacing={2}>
            {filterOptionsError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => fetchFilterOptions()}>
                    Reintentar
                  </Button>
                }
                sx={{ borderRadius: "12px" }}
              >
                {filterOptionsError}
              </Alert>
            ) : null}

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterListIcon sx={{ color: "primary.700" }} />
                <Typography sx={{ fontSize: 15, fontWeight: 900, color: "primary.900" }}>
                  Filtros de Solicitudes
                </Typography>
              </Stack>

              {hasActiveFilters ? (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  sx={{
                    borderRadius: "16px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 12,
                    color: "neutral.700",
                  }}
                >
                  Limpiar filtros
                </Button>
              ) : null}
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 1.5,
              }}
            >
              {/* Form Filter */}
              <FormControl fullWidth size="small">
                <InputLabel id="filter-form-label">Formulario</InputLabel>
                <Select
                  labelId="filter-form-label"
                  label="Formulario"
                  value={formIdFilter}
                  onChange={(e) => {
                    setFormIdFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Todos los formularios</MenuItem>
                  {filterOptions.forms.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Employee Filter */}
              <FormControl fullWidth size="small">
                <InputLabel id="filter-employee-label">Empleado</InputLabel>
                <Select
                  labelId="filter-employee-label"
                  label="Empleado"
                  value={employeeIdFilter}
                  onChange={(e) => {
                    setEmployeeIdFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Todos los empleados</MenuItem>
                  {filterOptions.employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.fullName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Sector Filter */}
              <FormControl fullWidth size="small">
                <InputLabel id="filter-sector-label">Sector</InputLabel>
                <Select
                  labelId="filter-sector-label"
                  label="Sector"
                  value={sectorFilter}
                  onChange={(e) => {
                    setSectorFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Todos los sectores</MenuItem>
                  {filterOptions.sectors.map((sec) => (
                    <MenuItem key={sec} value={sec}>
                      {sec}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status Filter */}
              <FormControl fullWidth size="small">
                <InputLabel id="filter-status-label">Estado</InputLabel>
                <Select
                  labelId="filter-status-label"
                  label="Estado"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Todos los estados</MenuItem>
                  <MenuItem value="IN_REVIEW">En revisión</MenuItem>
                  <MenuItem value="APPROVED">Aprobado</MenuItem>
                  <MenuItem value="CANCELLED">Cancelado</MenuItem>
                </Select>
              </FormControl>

              {/* Operational Date */}
              <TextField
                size="small"
                type="date"
                label="Fecha Operativa"
                InputLabelProps={{ shrink: true }}
                value={operationalDateFilter}
                onChange={(e) => {
                  setOperationalDateFilter(e.target.value);
                  setPage(0);
                }}
              />

              {/* Period Filter */}
              <FormControl fullWidth size="small">
                <InputLabel id="filter-period-label">Período / Cierre</InputLabel>
                <Select
                  labelId="filter-period-label"
                  label="Período / Cierre"
                  value={periodIdFilter}
                  onChange={(e) => {
                    setPeriodIdFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Todos los períodos</MenuItem>
                  {filterOptions.periods.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.generatedName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Search text */}
              <TextField
                size="small"
                label="Buscar..."
                placeholder="Nombre de empleado o formulario"
                value={searchFilter}
                onChange={(e) => {
                  setSearchFilter(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "neutral.500" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ gridColumn: { xs: "1fr", md: "span 2" } }}
              />
            </Box>
          </Stack>
        </Paper>

        {/* Submissions Table Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "18px",
            bgcolor: "rgba(255, 255, 255, 0.95)",
            border: "1px solid rgba(20, 32, 82, 0.08)",
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: 16, fontWeight: 900, color: "primary.900" }}>
                Bandeja de Respuestas ({totalItems})
              </Typography>
            </Stack>

            {error ? (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                {error}
              </Alert>
            ) : null}

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid rgba(20, 32, 82, 0.10)",
                borderRadius: "14px",
              }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: "neutral.100" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                      Formulario
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                      Empleado
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                      Sector
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                      Fecha Operativa
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                      Estado
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                      Período
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>
                      Enviado
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, fontSize: 12 }}>
                      Movimientos
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: 12 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={26} />
                        <Typography sx={{ fontSize: 12.5, color: "neutral.600", mt: 1 }}>
                          Cargando solicitudes...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : submissions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        align="center"
                        sx={{ py: 6, color: "neutral.600", fontSize: 13 }}
                      >
                        No se encontraron solicitudes recibidas con los filtros aplicados.
                      </TableCell>
                    </TableRow>
                  ) : (
                    submissions.map((sub) => (
                      <TableRow key={sub.id} hover>
                        <TableCell sx={{ fontSize: 12.5, fontWeight: 700, color: "primary.900" }}>
                          {sub.form?.name || `Formulario #${sub.form?.id}`}
                          <Chip
                            label={getFormTypeLabel(sub.form?.type)}
                            size="small"
                            sx={{ ml: 1, height: 18, fontSize: 9.5, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5 }}>
                          {sub.employee?.fullName || "—"}
                          {sub.employee?.dni ? (
                            <Typography sx={{ fontSize: 11, color: "neutral.600" }}>
                              DNI {sub.employee.dni}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5 }}>
                          {sub.sector || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5 }}>
                          {formatDate(sub.operationalDate)}
                        </TableCell>
                        <TableCell>
                          <SubmissionStatusChip status={sub.status} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {sub.period?.generatedName || "Sin asignar"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11.5, color: "neutral.700" }}>
                          {formatDateTime(sub.submittedAt)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${sub.movementsCount || 0}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: sub.movementsCount > 0 ? "secondary.100" : "neutral.200",
                              color: "primary.900",
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Ver respuesta">
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label={`Ver respuesta de ${sub.employee?.fullName || "empleado"}`}
                              onClick={() => setSelectedAssignmentId(sub.assignmentId)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={SUBMISSIONS_PAGE_SIZE}
              rowsPerPageOptions={[]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </Stack>
        </Paper>
      </Stack>

      {/* Shared Response Modal */}
      {selectedAssignmentId ? (
        <Suspense fallback={null}>
          <TreasurySubmissionDetailModal
            open
            assignmentId={selectedAssignmentId}
            onClose={() => setSelectedAssignmentId(null)}
            onSuccess={() => {
              fetchSubmissions();
              fetchFilterOptions();
            }}
          />
        </Suspense>
      ) : null}
    </Box>
  );
}
